import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlagStore } from "@/store/featureFlagStore";
import { getPublicMenuUrl } from "../tables/tableService";
import { detectUserLocation, LocationInfo } from "./locationService";

interface PaymentResponse {
  success: boolean;
  error?: string;
  checkoutUrl?: string;
  reference?: string;
  provider?: 'paystack' | 'stripe';
}

export const initiatePayment = async (
  orderId: string,
  storeId: string,
  amount: number,
  email: string,
  tableId?: string
): Promise<PaymentResponse> => {
  try {
    // Check if payment feature is enabled
    const isPaymentEnabled = useFeatureFlagStore
      .getState()
      .checkFlag("activate_payment_request");

    if (!isPaymentEnabled) {
      return { success: false, error: "Payment feature is not enabled" };
    }

    // Get store payment mode
    const { data: store, error: storeError } = await supabase
      .from("stores")
      .select("request_payment_mode")
      .eq("id", storeId)
      .single();

    if (storeError || !store) {
      console.error("Error fetching store:", storeError);
      return { success: false, error: "Store configuration not found" };
    }

    // Detect user location to determine payment provider
    const locationInfo = await detectUserLocation();
    
    if (locationInfo.paymentProvider === 'stripe') {
      // Use Stripe for international payments
      return await initiateStripePayment(orderId, storeId, amount, email, tableId);
    } else {
      // Use Paystack for African countries
      return await initiatePaystackPayment(orderId, storeId, amount, email, tableId);
    }
  } catch (error) {
    console.error("Payment initiation error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown payment error",
    };
  }
};

export const updateOrderPaymentStatus = async (
  orderId: string,
  isPaid: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from("orders")
      .update({ is_payed: isPaid })
      .eq("id", orderId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error updating payment status:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to update payment status",
    };
  }
};

async function initiatePaystackPayment(
  orderId: string,
  storeId: string,
  amount: number,
  email: string,
  tableId?: string
): Promise<PaymentResponse> {
  try {
    // Get the current URL and ensure table context is preserved
    const callbackUrl = `${window.location.origin}/payment/callback?table=${tableId}`;

    // Initialize payment with Paystack via Supabase Edge Function
    const { data, error: functionError } = await supabase.functions.invoke(
      "paystack-initialize",
      {
        body: JSON.stringify({
          amount,
          email,
          orderId,
          storeId,
          tableId,
          callbackUrl,
        }),
      }
    );

    if (functionError) {
      throw new Error(functionError.message || "Failed to initialize Paystack payment");
    }

    return {
      success: true,
      checkoutUrl: data.checkoutUrl,
      reference: data.reference,
      provider: 'paystack'
    };
  } catch (error) {
    console.error("Paystack payment initiation error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Paystack payment failed",
      provider: 'paystack'
    };
  }
}

async function initiateStripePayment(
  orderId: string,
  storeId: string,
  amount: number,
  email: string,
  tableId?: string
): Promise<PaymentResponse> {
  try {
    // Ensure we have a valid tableId
    if (!tableId) {
      throw new Error("Table ID is required for Stripe payment");
    }

    // Use only the path for the return URL
    const returnUrl = `/payment/callback`;
    
    // Debug log
    console.log('Stripe payment return URL:', returnUrl);
    console.log('Stripe payment request body:', {
      orderId,
      storeId,
      amount,
      customerEmail: email,
      tableId,
      returnUrl,
    });
    
    // Initialize payment with Stripe via Supabase Edge Function
    const { data, error: functionError } = await supabase.functions.invoke(
      "stripe-initialize",
      {
        body: JSON.stringify({
          orderId,
          storeId,
          amount,
          customerEmail: email,
          tableId,
          returnUrl,
        }),
      }
    );

    if (functionError) {
      throw new Error(functionError.message || "Failed to initialize Stripe payment");
    }

    return {
      success: true,
      checkoutUrl: data.checkoutUrl,
      reference: data.sessionId,
      provider: 'stripe'
    };
  } catch (error) {
    console.error("Stripe payment initiation error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Stripe payment failed",
      provider: 'stripe'
    };
  }
}
